import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, Trash2, TrendingUp, TrendingDown } from "lucide-react";
import { money } from "@/lib/format";

export default function LFPriceIntel() {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({}); // material_id -> {name, price}

  const load = () => api.get("/lf/price-intel").then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const addComp = async (mid) => {
    const d = draft[mid] || {};
    if (!d.name || !d.price) { toast.error("Enter competitor name and price"); return; }
    try {
      await api.post("/competitor-prices", { material_id: mid, competitor_name: d.name, price_per_sqft: Number(d.price) });
      setDraft({ ...draft, [mid]: { name: "", price: "" } });
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const rmComp = async (cid) => { try { await api.delete(`/competitor-prices/${cid}`); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };

  if (!data) return null;
  return (
    <div data-testid="lf-price-intel" className="space-y-4">
      <p className="text-xs text-slate-500">Cost = material + labor + ink (set the last two in Settings → Large Format Pricing). Suggested = cost × retail markup. "Your price" = the material's Override $/ft². Register what competitors charge to compare.</p>
      {data.materials.map((m) => {
        const d = draft[m.material_id] || { name: "", price: "" };
        const beatsComp = m.competitor_avg != null && m.override_retail != null && m.override_retail <= m.competitor_avg;
        return (
          <div key={m.material_id} data-testid="intel-material" className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="font-head font-bold text-sm">{m.name} <span className="text-[10px] font-mono uppercase text-slate-400 ml-1">{m.lf_subtype}</span></div>
              <div className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full ${m.profitable ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`} data-testid="intel-profit-badge">
                {m.profitable ? <TrendingUp size={13} /> : <TrendingDown size={13} />}
                {m.profitable ? `Profit ${money(m.profit_per_sqft)}/ft² · margin ${m.margin_pct}%` : "Below cost!"}
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 text-sm">
              <div><div className="text-[10px] font-mono uppercase text-slate-400">Cost /ft²</div><div className="num font-semibold">{money(m.cost_per_sqft)}</div></div>
              <div><div className="text-[10px] font-mono uppercase text-slate-400">Suggested /ft²</div><div className="num font-semibold text-slate-500">{money(m.suggested_retail)}</div></div>
              <div><div className="text-[10px] font-mono uppercase text-slate-400">Your price /ft²</div><div className="num font-black text-[#2495D3]">{m.override_retail != null ? money(m.override_retail) : "—"}</div></div>
              <div><div className="text-[10px] font-mono uppercase text-slate-400">Competitor avg</div><div className={`num font-semibold ${beatsComp ? "text-emerald-600" : m.competitor_avg != null ? "text-amber-600" : "text-slate-400"}`}>{m.competitor_avg != null ? money(m.competitor_avg) : "—"}</div></div>
            </div>
            <div className="mt-3 border-t border-slate-100 pt-3">
              <div className="text-[10px] font-mono uppercase text-slate-400 mb-1.5">Competitors</div>
              <div className="space-y-1.5">
                {m.competitors.map((c) => (
                  <div key={c.id} data-testid="intel-comp-row" className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">{c.competitor_name}</span>
                    <span className="flex items-center gap-3">
                      <span className="num font-semibold">{money(c.price_per_sqft)}/ft²</span>
                      <button data-testid="intel-comp-remove" onClick={() => rmComp(c.id)} className="text-slate-300 hover:text-red-500"><Trash2 size={14} /></button>
                    </span>
                  </div>
                ))}
                {m.competitors.length === 0 && <div className="text-xs text-slate-400">No competitors yet.</div>}
              </div>
              <div className="flex items-center gap-2 mt-2">
                <Input data-testid="intel-comp-name" value={d.name} onChange={(e) => setDraft({ ...draft, [m.material_id]: { ...d, name: e.target.value } })} placeholder="Competitor (e.g. Star Sign)" className="h-8 text-xs rounded-lg flex-1" />
                <Input data-testid="intel-comp-price" type="number" step="0.01" value={d.price} onChange={(e) => setDraft({ ...draft, [m.material_id]: { ...d, price: e.target.value } })} placeholder="$/ft²" className="h-8 text-xs rounded-lg w-24 num" />
                <Button data-testid="intel-comp-add" size="sm" variant="outline" className="h-8 text-xs rounded-lg" onClick={() => addComp(m.material_id)}><Plus size={14} className="mr-1" />Add</Button>
              </div>
            </div>
          </div>
        );
      })}
      {data.materials.length === 0 && <div className="text-center text-slate-400 py-10">No roll materials assigned to Large Format.</div>}
    </div>
  );
}
